import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle, CheckCircle2, Clock, Milk, Plus, Stethoscope, Wallet } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  activeWithdrawal,
  buyerLedger,
  dailyTotals,
  dropPercent,
  enabledSessions,
  isMilkDrop,
  milkingCowsOn,
  missedSessions,
  moneyStats,
  reconcileMilkDay,
  sessionAverage,
  sessionCutoff,
  type DomainCategory,
  type DomainEvent,
  type DomainExpense,
  type DomainMilkRecord,
  type DomainPayment,
  type DomainSale,
} from '@dairy/domain';
import { addDays, FARM_TIME_ZONE, firstOfMonth, localDateTimeInTimeZone, monthOf, type MilkSession } from '@dairy/shared';
import { useIsOwner, useSettings } from '@/auth/useSession';
import { Badge, Button, Card, CardTitle, buttonVariants } from '@/components/ui';
import { db, getMeta, setMeta } from '@/db/db';
import { useHerd, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useMilkRecords, useMilkSales, useUsage } from '@/features/milk/useMilk';
import { reminderBuckets } from '@/features/reminders/RemindersPage';

/** Screen 2 — Today, the daily cockpit (RP-1 Today v1; MR-5, MR-7, BR-9, HE-2, IN-6, IN-7). */
export function TodayPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const isOwner = useIsOwner();
  const today = useToday();
  const nowLocal = localDateTimeInTimeZone(FARM_TIME_ZONE);
  const herd = useHerd(today);
  const weekAgo = addDays(today, -8);
  const records = useMilkRecords(addDays(today, -15), today) ?? [];
  const usage = useUsage(weekAgo, today) ?? [];
  const sales = useMilkSales(weekAgo, today) ?? [];
  const reminders = useLiveQuery(() => db.reminders.where('status').equals('OPEN').toArray(), []) ?? [];
  const seen = useLiveQuery(() => getMeta<string[]>('seenDrops'), []) ?? [];
  const animals = herd.rows.map((r) => r.animal);
  const events = useMemo(() => [...herd.eventsByAnimal.values()].flat(), [herd.eventsByAnimal]);

  const milking = useMemo(() => milkingCowsOn(animals, herd.eventsByAnimal, today, s), [animals, herd.eventsByAnimal, today, s]);
  const todayTotals = dailyTotals(records, usage, today);
  const yesterdayTotals = dailyTotals(records, usage, addDays(today, -1));

  const sessions = enabledSessions(s).map((ses) => {
    const recs = records.filter((r) => r.date === today && r.session === ses.session);
    const cutoff = sessionCutoff(today, ses.session, s)!;
    const missing = milking.filter((c) => !recs.some((r) => r.animalId === c.id)).length;
    const state = recs.length > 0 && missing === 0 ? 'done' : nowLocal > cutoff && milking.length > 0 ? 'missed' : 'due';
    return { ...ses, ml: recs.reduce((a, r) => a + r.ml, 0), count: recs.length, missing, state };
  });

  const missed = missedSessions(animals, events as DomainEvent[], records as DomainMilkRecord[], s, nowLocal);

  // MR-5: the cow's latest record is a drop vs her 7-day average for that session.
  const drops = milking
    .map((cow) => {
      const latest = records.filter((r) => r.animalId === cow.id && r.date >= addDays(today, -1)).sort((a, b) => (a.date + a.session < b.date + b.session ? 1 : -1))[0];
      if (!latest || seen.includes(latest.id)) return null;
      const avg = sessionAverage(records, cow.id, latest.session, latest.date);
      return isMilkDrop(latest.ml, avg, s.milkDropThreshold) ? { cow, rec: latest, pct: dropPercent(latest.ml, avg.avgMl) } : null;
    })
    .filter((x): x is NonNullable<typeof x> => !!x);

  const withdrawals = herd.rows
    .filter((r) => r.animal.status === 'ACTIVE')
    .map((r) => ({ animal: r.animal, w: activeWithdrawal(r.events, today) }))
    .filter((x) => x.w);

  const unaccounted = Array.from({ length: 7 }, (_, i) => addDays(today, -i - 1))
    .map((d) => {
      const tot = dailyTotals(records, usage, d);
      if (tot.producedMl === 0) return null;
      const sold = sales.filter((x) => x.date === d).reduce((a, x) => a + (x.milkMl ?? 0), 0);
      const r = reconcileMilkDay(tot.saleableMl, sold, s.unaccountedMilkThreshold);
      return r.flag === 'OK' ? null : { date: d, ...r };
    })
    .filter((x): x is NonNullable<typeof x> => !!x);

  const buckets = reminderBuckets(reminders, today);
  const lateEntries = records.filter((r) => r.lateEntry && r.date >= addDays(today, -7));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Link to="/milk/session" className={cn(buttonVariants({ size: 'lg' }), 'col-span-2')}>
          <Milk className="h-6 w-6" /> {t('today.recordMilk')}
        </Link>
        <Link to="/milk/usage" className={buttonVariants({ variant: 'secondary' })}>
          {t('today.milkUsage')}
        </Link>
        <Link to="/events/new" className={buttonVariants({ variant: 'secondary' })}>
          <Stethoscope className="h-5 w-5" /> {t('today.recordEvent')}
        </Link>
      </div>

      <Card>
        <CardTitle>{t('today.sessions')}</CardTitle>
        <div className="grid gap-2">
          {sessions.map((ses) => (
            <Link
              key={ses.session}
              to={`/milk/session?date=${today}&session=${ses.session}`}
              className="flex min-h-14 items-center gap-3 rounded-xl border border-stone-200 px-3"
              data-testid={`session-${ses.session}`}
            >
              {ses.state === 'done' ? (
                <CheckCircle2 className="h-6 w-6 text-green-700" />
              ) : ses.state === 'missed' ? (
                <AlertTriangle className="h-6 w-6 text-red-700" />
              ) : (
                <Clock className="h-6 w-6 text-stone-500" />
              )}
              <div className="flex-1">
                <div className="font-semibold">
                  {t(`milk.session.${ses.session}`)} · {ses.time}
                </div>
                <div className="text-sm text-stone-600">
                  {ses.state === 'done'
                    ? t('today.sessionDone', { liters: f.liters(ses.ml), count: ses.count })
                    : ses.state === 'missed'
                      ? t('today.sessionMissed', { count: ses.missing })
                      : ses.count > 0
                        ? t('today.sessionPartial', { count: ses.count, total: milking.length })
                        : t('today.sessionDue')}
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-center">
          <div className="rounded-xl bg-stone-100 p-3">
            <div className="text-sm text-stone-600">{t('today.totalToday')}</div>
            <div className="text-2xl font-bold tabular-nums">{f.liters(todayTotals.producedMl)}</div>
          </div>
          <div className="rounded-xl bg-stone-100 p-3">
            <div className="text-sm text-stone-600">{t('today.totalYesterday')}</div>
            <div className="text-2xl font-bold tabular-nums">{f.liters(yesterdayTotals.producedMl)}</div>
          </div>
        </div>
      </Card>

      {drops.length || missed.length ? (
        <Card>
          <CardTitle>{t('today.alerts')}</CardTitle>
          <ul className="space-y-2">
            {missed.map((m) => (
              <li key={`${m.date}-${m.session}`}>
                <Link to={`/milk/session?date=${m.date}&session=${m.session}`} className="flex min-h-12 items-center gap-2 rounded-xl bg-red-50 px-3 text-red-900">
                  <AlertTriangle className="h-5 w-5" />
                  {t('today.missed', { session: t(`milk.session.${m.session}`), date: f.short(m.date), count: m.missingCount })}
                </Link>
              </li>
            ))}
            {drops.map((d) => (
              <li key={d.rec.id} className="flex min-h-12 items-center gap-2 rounded-xl bg-amber-50 px-3 text-amber-950">
                <span className="flex-1">
                  {t('today.drop', { tag: d.cow.tag, session: t(`milk.session.${d.rec.session as MilkSession}`), pct: d.pct })}
                </span>
                <Button size="sm" variant="secondary" onClick={() => void setMeta('seenDrops', [...seen, d.rec.id].slice(-200))}>
                  {t('today.seen')}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <CardTitle className="mb-0">{t('today.reminders')}</CardTitle>
          <Link to="/reminders" className="text-sm font-semibold text-brand-800">
            {t('common.seeAll')}
          </Link>
        </div>
        <div className="mb-3 flex gap-2">
          <Badge tone={buckets.overdue.length ? 'red' : 'neutral'}>{t('reminders.overdueCount', { count: buckets.overdue.length })}</Badge>
          <Badge tone={buckets.today.length ? 'amber' : 'neutral'}>{t('reminders.todayCount', { count: buckets.today.length })}</Badge>
        </div>
        <ul className="divide-y divide-stone-100">
          {[...buckets.overdue, ...buckets.today, ...buckets.upcoming].slice(0, 5).map((r) => (
            <li key={r.id} className="flex min-h-12 items-center gap-2 py-1">
              <span className="font-semibold">{herd.byId.get(r.animalId)?.animal.tag}</span>
              <span className="flex-1 text-stone-700">{t(`reminders.type.${r.type}`)}</span>
              <span className={cn('text-sm', r.dueDate < today ? 'text-red-700' : 'text-stone-600')}>{f.short(r.dueDate)}</span>
            </li>
          ))}
        </ul>
      </Card>

      {withdrawals.length ? (
        <Card>
          <CardTitle>{t('today.onWithdrawal')}</CardTitle>
          <ul className="space-y-1">
            {withdrawals.map(({ animal, w }) => (
              <li key={animal.id}>
                <Link to={`/animals/${animal.id}`} className="block min-h-11 py-2">
                  {t('today.withdrawalUntil', { tag: animal.tag, date: f.short(w!.until) })}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {unaccounted.length ? (
        <Card>
          <CardTitle>{t('today.unaccountedTitle')}</CardTitle>
          <ul className="space-y-1">
            {unaccounted.map((u) => (
              <li key={u.date}>
                <Link to={`/milk/day/${u.date}`} className="block min-h-11 py-2">
                  {f.short(u.date)}: {u.flag === 'OVERSOLD' ? t('milk.oversold') : t('milk.unaccounted', { liters: f.liters(u.unaccountedMl) })}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {isOwner ? <OwnerMoneyCards today={today} /> : null}

      {isOwner && lateEntries.length ? (
        <Card>
          <CardTitle>{t('today.lateEntries')}</CardTitle>
          <p className="text-stone-700">{t('today.lateEntriesCount', { count: lateEntries.length })}</p>
        </Card>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Link to="/sales/new" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          <Plus className="h-4 w-4" /> {t('money.recordSale')}
        </Link>
        <Link to="/expenses/new" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          <Wallet className="h-4 w-4" /> {t('expenses.add')}
        </Link>
      </div>
    </div>
  );
}

/** S4-T12 owner money cards: this month's income, operating expenses (accrual), profit, receivables, overdue buyers. */
function OwnerMoneyCards({ today }: { today: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const data = useLiveQuery(async () => {
    const [sales, payments, expenses, categories, buyers] = await Promise.all([
      db.sales.toArray(),
      db.payments.toArray(),
      db.expenses.toArray(),
      db.expenseCategories.toArray(),
      db.buyers.toArray(),
    ]);
    return { sales, payments, expenses, categories, buyers };
  }, []);
  if (!data) return null;
  const month = monthOf(today);
  const m = moneyStats({
    sales: data.sales as unknown as DomainSale[],
    payments: data.payments as unknown as DomainPayment[],
    expenses: data.expenses as unknown as DomainExpense[],
    categories: data.categories as unknown as DomainCategory[],
    from: firstOfMonth(month),
    to: today,
  });
  let total = 0;
  let overdue = 0;
  const overdueBuyers: Array<{ id: string; name: string; santim: number; age: number }> = [];
  for (const b of data.buyers) {
    const l = buyerLedger(
      data.sales.filter((x) => x.buyerId === b.id) as unknown as DomainSale[],
      data.payments.filter((x) => x.buyerId === b.id) as unknown as DomainPayment[],
      today,
      s.receivableOverdueDays,
    );
    total += l.unpaid.reduce((a, u) => a + u.unpaidSantim, 0);
    overdue += l.overdueSantim;
    if (l.overdueSantim > 0) overdueBuyers.push({ id: b.id, name: b.name, santim: l.overdueSantim, age: l.oldestUnpaidAgeDays ?? 0 });
  }
  return (
    <Card>
      <CardTitle>{t('today.moneyThisMonth')}</CardTitle>
      <dl className="grid grid-cols-2 gap-3">
        <div>
          <dt className="text-sm text-stone-600">{t('today.income')}</dt>
          <dd className="text-lg font-bold tabular-nums">{f.birr(m.incomeSantim)}</dd>
        </div>
        <div>
          <dt className="text-sm text-stone-600">{t('today.operatingExpenses')}</dt>
          <dd className="text-lg font-bold tabular-nums">{f.birr(m.operatingCostSantim)}</dd>
        </div>
        <div>
          <dt className="text-sm text-stone-600">{t('today.operatingProfit')}</dt>
          <dd className={cn('text-lg font-bold tabular-nums', m.operatingProfitSantim < 0 && 'text-red-700')}>{f.birr(m.operatingProfitSantim)}</dd>
        </div>
        <div>
          <dt className="text-sm text-stone-600">{t('today.receivables')}</dt>
          <dd className="text-lg font-bold tabular-nums">{f.birr(total)}</dd>
          {overdue > 0 ? <dd className="text-sm text-red-700">{t('today.overdueAmount', { amount: f.birr(overdue) })}</dd> : null}
        </div>
      </dl>
      {overdueBuyers.length ? (
        <ul className="mt-3 space-y-1 border-t border-stone-100 pt-3">
          {overdueBuyers.map((b) => (
            <li key={b.id}>
              <Link to={`/buyers/${b.id}`} className="flex min-h-11 items-center justify-between gap-2">
                <span>{b.name}</span>
                <span className="text-red-700">{t('today.overdueBuyer', { amount: f.birr(b.santim), days: b.age })}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
